/* eslint-disable max-lines */
/* eslint-disable complexity */
import { ChevronSmallDown, ChevronSmallUp, type SvgIconT } from '@elliemae/ds-icons';
import React, { useCallback, useContext, useState } from 'react';
import { ControlledDateTimePickerDatatestid } from '../../../constants/index.js';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import { StyledTimeBtn, StyledWheelChangeTimeBtn, StyledWheelList, StyledWheelListItem } from './Styleds.js';
import { TimeWheelContext } from './TimeWheelContext.js';

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
  pointerEvents: 'none',
};

/**
 * A11y layer: the true semantic contract for screen readers and keyboard users.
 * It is visually hidden and pointer-event-free — it exists solely to provide a
 * valid, focused listbox with two options that AT can read and keyboard can operate.
 * The visual layer below is fully aria-hidden; this layer is the single source of
 * truth for all assistive technology interaction.
 */
const MeridiemA11yLayer = (): JSX.Element => {
  const { currMeridiem, handleCurrMeridiemOnKeyDown } = useContext(TimeWheelContext);
  const {
    currMeridiemBtnRef,
    meridiem,
    instanceUid,
    latestInteractionRegion,
    currFocusDescriber,
    getIsDisabledTime,
    hours,
    minutes,
    props: { readOnly, applyAriaDisabled },
  } = useContext(ControlledDateTimePickerContext);

  const isAmCurrent = !currMeridiem || currMeridiem === 'AM';
  const amId = `${instanceUid}-meridiem-am`;
  const pmId = `${instanceUid}-meridiem-pm`;
  const hourMinuteString = `${hours || '01'}:${minutes || '00'}`;
  const isCurrentMeridiemInvalid = isAmCurrent
    ? getIsDisabledTime(`${hourMinuteString} AM`)
    : getIsDisabledTime(`${hourMinuteString} PM`);

  const handleListboxRef = useCallback(
    (listboxDomNode: HTMLDivElement) => {
      setTimeout(() => {
        currMeridiemBtnRef.current = listboxDomNode;
        if (latestInteractionRegion === 'timewheel' && currFocusDescriber === 'timewheel-curr-meridiem')
          listboxDomNode?.focus?.();
      });
    },
    [currFocusDescriber, latestInteractionRegion, currMeridiemBtnRef],
  );

  return (
    <div
      role="listbox"
      aria-label="Meridiem"
      aria-orientation="vertical"
      aria-disabled={readOnly || applyAriaDisabled}
      aria-invalid={isCurrentMeridiemInvalid}
      aria-activedescendant={isAmCurrent ? amId : pmId}
      tabIndex={0}
      onKeyDown={handleCurrMeridiemOnKeyDown}
      ref={handleListboxRef}
      style={srOnlyStyle}
      data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.CURRENT_MERIDIEM}
    >
      <div role="option" id={amId} aria-selected={meridiem === 'AM'}>
        AM
      </div>
      <div role="option" id={pmId} aria-selected={meridiem === 'PM'}>
        PM
      </div>
    </div>
  );
};

/**
 * Visual layer: the presentation surface that mouse users see and interact with.
 * All elements are aria-hidden — AT never reaches this layer. The chevron buttons
 * and AM/PM items are click-only affordances; on click they update state and redirect
 * focus to the a11y layer above so keyboard can continue from there. No ARIA roles,
 * labels, or keyboard handlers live here.
 */
const MeridiemVisualLayer = ({ isFocusedWheel }: { isFocusedWheel: boolean }): JSX.Element => {
  const { currMeridiem, handleTimeWheelBtnChangeMeridiem } = useContext(TimeWheelContext);
  const {
    prevMeridiemBtnRef,
    currMeridiemBtnRef,
    nextMeridiemBtnRef,
    getIsDisabledTime,
    hours,
    minutes,
    meridiem,
    getProps,
    props: { readOnly, applyAriaDisabled },
  } = useContext(ControlledDateTimePickerContext);

  const isAmCurrent = !currMeridiem || currMeridiem === 'AM';
  const isPmCurrent = currMeridiem === 'PM';
  const hourMinuteString = `${hours || '01'}:${minutes || '00'}`;
  const isNotAValidAm = getIsDisabledTime(`${hourMinuteString} AM`);
  const isNotAValidPm = getIsDisabledTime(`${hourMinuteString} PM`);

  const hasDisabledStyles = readOnly || applyAriaDisabled;
  const arrowColor = !hasDisabledStyles ? (['brand-primary', '700'] as SvgIconT.ColorType) : undefined;

  return (
    <>
      <StyledWheelListItem>
        <StyledWheelChangeTimeBtn
          aria-label="set am meridiem"
          data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.PREV_MERIDIEM}
          buttonType="raw"
          size="m"
          onClick={(e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
            handleTimeWheelBtnChangeMeridiem('AM', e);
            currMeridiemBtnRef.current?.focus();
          }}
          innerRef={prevMeridiemBtnRef}
          tabIndex={-1}
          type="button"
          applyAriaDisabled={applyAriaDisabled}
          readOnly={readOnly}
          getOwnerProps={getProps}
        >
          <ChevronSmallUp color={arrowColor} />
        </StyledWheelChangeTimeBtn>
      </StyledWheelListItem>
      {currMeridiem === 'AM' || currMeridiem === '' ? <StyledWheelListItem aria-hidden="true" /> : null}
      <StyledWheelListItem aria-hidden="true">
        <StyledTimeBtn
          $isTheItemVisuallyInTheCenter={isAmCurrent}
          $isNotAValidSelection={isNotAValidAm}
          $selected={isAmCurrent && !!meridiem}
          $isUnset={!meridiem}
          $isFocusedWheel={isFocusedWheel}
          data-testid={
            ControlledDateTimePickerDatatestid.TIMEWHEEL[
              isAmCurrent ? 'CURRENT_MERIDIEM_VISUAL_ELEMENT' : 'MERIDIEM_VISUAL_ELEMENT'
            ]
          }
          onClick={(e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
            handleTimeWheelBtnChangeMeridiem('AM', e);
            currMeridiemBtnRef.current?.focus();
          }}
          getOwnerProps={getProps}
          $readOnly={readOnly || applyAriaDisabled}
        >
          AM
        </StyledTimeBtn>
      </StyledWheelListItem>
      <StyledWheelListItem aria-hidden="true">
        <StyledTimeBtn
          $isTheItemVisuallyInTheCenter={isPmCurrent}
          $selected={isPmCurrent && !!meridiem}
          $isNotAValidSelection={isNotAValidPm}
          $isUnset={!meridiem}
          $isFocusedWheel={isFocusedWheel}
          data-testid={
            ControlledDateTimePickerDatatestid.TIMEWHEEL[
              isPmCurrent ? 'CURRENT_MERIDIEM_VISUAL_ELEMENT' : 'MERIDIEM_VISUAL_ELEMENT'
            ]
          }
          onClick={(e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
            handleTimeWheelBtnChangeMeridiem('PM', e);
            currMeridiemBtnRef.current?.focus();
          }}
          getOwnerProps={getProps}
          $readOnly={readOnly || applyAriaDisabled}
        >
          PM
        </StyledTimeBtn>
      </StyledWheelListItem>
      {currMeridiem === 'PM' ? <StyledWheelListItem aria-hidden="true" /> : null}
      <StyledWheelListItem>
        <StyledWheelChangeTimeBtn
          aria-label="set pm meridiem"
          data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.NEXT_MERIDIEM}
          buttonType="raw"
          size="m"
          onClick={(e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
            handleTimeWheelBtnChangeMeridiem('PM', e);
            currMeridiemBtnRef.current?.focus();
          }}
          innerRef={nextMeridiemBtnRef}
          tabIndex={-1}
          type="button"
          applyAriaDisabled={applyAriaDisabled}
          readOnly={readOnly}
          getOwnerProps={getProps}
        >
          <ChevronSmallDown color={arrowColor} />
        </StyledWheelChangeTimeBtn>
      </StyledWheelListItem>
    </>
  );
};

export const MeridiemList = (): JSX.Element => {
  const [isFocusedWheel, setIsFocusedWheel] = useState(false);
  const { trackFocusTimewheelCurrMeridiem } = useContext(ControlledDateTimePickerContext);

  const handleWheelFocus = useCallback(() => {
    setIsFocusedWheel(true);
    trackFocusTimewheelCurrMeridiem();
  }, [setIsFocusedWheel, trackFocusTimewheelCurrMeridiem]);

  const handleWheelBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocusedWheel(false);
    },
    [setIsFocusedWheel],
  );

  return (
    <StyledWheelList onFocus={handleWheelFocus} onBlur={handleWheelBlur}>
      <MeridiemA11yLayer />
      <MeridiemVisualLayer isFocusedWheel={isFocusedWheel} />
    </StyledWheelList>
  );
};

export default MeridiemList;
